import i18n from 'i18next';
import { FieldPath } from 'react-hook-form';
import { array, boolean, InferType, number, object, string } from 'yup';

import type { ChartOfAccountsResponse, SharedCostResponse } from '@client/api';
import { transformToOptionalNumber } from '@client/toolkit';

import { ALLOCATION_METHOD_BY_API_VALUE, COST_CLASS_BY_API_VALUE } from './shared-cost.mapping';
import {
  ALLOCATION_KEY_IDS,
  ALLOCATION_METHODS,
  COST_CLASSIFICATIONS,
  MANAGEMENT_MODELS,
  MockPortfolioEconomySettings,
} from './mock/portfolio-economy.mock';

/**
 * Empty input casts to `NaN`, which surfaces as a confusing "not a number" error;
 * the shared transform normalises it so `required()` speaks instead.
 */
const optionalNumber = () => number().transform(transformToOptionalNumber);

/** yup's locale overrides cover required/notType/oneOf but not min/max — translate those here. */
const tEconomy = (key: string, values?: Record<string, unknown>) =>
  i18n.t(key, { ns: 'economy', ...values });
const atLeast = (min: number) => () => tEconomy('settings.validation.min', { min });
const atMost = (max: number) => () => tEconomy('settings.validation.max', { max });

/**
 * Account numbers have to be unique within their own table. The test walks out to the form
 * root, which is that table's array and nothing else, because every card owns its own form.
 */
const uniqueAccountNumber = (arrayKey: string) => ({
  name: 'uniqueAccountNumber',
  message: () => tEconomy('settings.groupAccounts.duplicateAccountNumber'),
  test(this: { from?: { value: unknown }[] }, value: string | undefined) {
    const root = this.from?.[this.from.length - 1]?.value as Record<string, { accountNumber?: string }[]>;
    const rows = root?.[arrayKey];
    if (rows == null || value == null) return true;

    const normalized = value.trim().toLowerCase();
    return rows.filter((row) => row.accountNumber?.trim().toLowerCase() === normalized).length <= 1;
  },
});

const groupAccountSchema = object({
  id: string().required(),
  accountNumber: string().required().test(uniqueAccountNumber('groupAccounts')),
  name: string().required(),
  classification: string().oneOf([...COST_CLASSIFICATIONS]).required(),
  amount: optionalNumber().required(),
  method: string().oneOf([...ALLOCATION_METHODS]).required(),
  active: boolean().required(),
});

/**
 * One schema per editable card: Konsern-konti and Globale standarder each own their form,
 * so saving one section never validates or submits the other. Nothing crosses the boundary —
 * `marginPercent` only depends on `managementModel` (both in Globale standarder), and the
 * account-number uniqueness test only compares rows inside `groupAccounts`.
 *
 * Konsern-nivå and Allokering-metoder have no editable field, so they have no schema.
 */
export const groupAccountsSchema = object({
  groupAccounts: array().of(groupAccountSchema).required(),
});

export const commonCostChartSchema = object({
  commonCostChart: array()
    .of(
      object({
        id: string().required(),
        accountNumber: string().required().test(uniqueAccountNumber('commonCostChart')),
        name: string().required(),
        // A cost_categories.id, not one of ALLOCATION_KEY_IDS: the categories are tenant-owned,
        // editable rows now. ALLOCATION_KEY_IDS stays in the mock -- GLOBALE STANDARDER still binds
        // its Fordelingsnokkel weights to it.
        costCategoryId: string().required(),
        classification: string().oneOf([...COST_CLASSIFICATIONS]).required(),
      }),
    )
    .required(),
});

export const ownerCostAccountsSchema = object({
  ownerCostAccounts: array()
    .of(
      object({
        id: string().required(),
        accountNumber: string().required().test(uniqueAccountNumber('ownerCostAccounts')),
        name: string().required(),
        // A cost_categories.id. OWNER_COST_CATEGORIES is gone -- the categories are tenant-owned
        // rows with free-text names now.
        costCategoryId: string().required(),
        active: boolean().required(),
      }),
    )
    .required(),
});

export const globalStandardsSchema = object({
  managementModel: string().oneOf([...MANAGEMENT_MODELS]).required(),
  marginPercent: optionalNumber()
    .nullable()
    .when('managementModel', {
      is: 'ownerMargin',
      then: (schema) => schema.required().min(0, atLeast(0)).max(100, atMost(100)),
      otherwise: (schema) => schema.nullable(),
    }),
  markupRate: optionalNumber().required().min(0, atLeast(0)).max(100, atMost(100)),
  allocationKeys: array()
    .of(
      object({
        id: string().oneOf([...ALLOCATION_KEY_IDS]).required(),
        factor: optionalNumber().required().min(0, atLeast(0)),
      }),
    )
    .required(),
});

export type GroupAccountsFormValues = InferType<typeof groupAccountsSchema>;
export type GroupAccountsFormPath = FieldPath<GroupAccountsFormValues>;
export type GlobalStandardsFormValues = InferType<typeof globalStandardsSchema>;
export type GlobalStandardsFormPath = FieldPath<GlobalStandardsFormValues>;
export type GroupAccountFormValues = GroupAccountsFormValues['groupAccounts'][number];

export type CommonCostChartFormValues = InferType<typeof commonCostChartSchema>;
export type CommonCostChartFormPath = FieldPath<CommonCostChartFormValues>;
export type ChartLineFormValues = CommonCostChartFormValues['commonCostChart'][number];

export type OwnerCostAccountsFormValues = InferType<typeof ownerCostAccountsSchema>;
export type OwnerCostAccountsFormPath = FieldPath<OwnerCostAccountsFormValues>;
export type OwnerCostAccountFormValues = OwnerCostAccountsFormValues['ownerCostAccounts'][number];

/**
 * Konsern-konti come from the API, so this converter translates rather than copies — the other
 * three cards still read the mock and their converters are unchanged.
 */
export const toGroupAccountsFormValues = (accounts: SharedCostResponse[]): GroupAccountsFormValues => ({
  groupAccounts: accounts.map((account) => ({
    id: account.id,
    accountNumber: account.accountNumber,
    name: account.name,
    classification: COST_CLASS_BY_API_VALUE[account.costClass],
    amount: account.amount,
    method: ALLOCATION_METHOD_BY_API_VALUE[account.allocationMethod],
    active: account.isActive,
  })),
});

/** Flattens the grouped felleskost payload into the flat array the grid's field array wants. */
export const toCommonCostChartFormValues = (
  chart: ChartOfAccountsResponse | undefined,
): CommonCostChartFormValues => ({
  commonCostChart: (chart?.commonCost.categories ?? []).flatMap((category) =>
    (category.accounts ?? []).map((account) => ({
      id: account.id,
      accountNumber: account.accountNumber,
      name: account.name,
      costCategoryId: category.id,
      // Constant, and not read from the row: within this card the classification cannot be
      // anything else, which is why the API does not send it per account.
      classification: 'commonCost' as const,
    })),
  ),
});

/** The eierkost chart is already flat; this only renames fields onto the form's shape. */
export const toOwnerCostAccountsFormValues = (
  chart: ChartOfAccountsResponse | undefined,
): OwnerCostAccountsFormValues => ({
  ownerCostAccounts: (chart?.ownerCost.accounts ?? []).map((account) => ({
    id: account.id,
    accountNumber: account.accountNumber,
    name: account.name,
    costCategoryId: account.categoryId,
    active: account.isActive,
  })),
});

export const toGlobalStandardsFormValues = (settings: MockPortfolioEconomySettings): GlobalStandardsFormValues => ({
  managementModel: settings.globalStandards.managementModel,
  marginPercent: settings.globalStandards.marginPercent,
  markupRate: settings.globalStandards.markupRate,
  allocationKeys: settings.globalStandards.allocationKeys.map((key) => ({ ...key })),
});

let newAccountSequence = 0;

/** A blank row for the konsern-konti table. Ids only need to be unique within the form. */
/** A blank felleskost line. The caller supplies the category it lands in; it can be moved after. */
export const createEmptyChartLine = (costCategoryId: string): ChartLineFormValues => ({
  id: `new-chart-line-${++newAccountSequence}`,
  accountNumber: '',
  name: '',
  costCategoryId,
  classification: 'commonCost',
});

/** A blank eierkost account. The caller supplies the category it lands in. */
export const createEmptyOwnerCostAccount = (costCategoryId: string): OwnerCostAccountFormValues => ({
  id: `new-owner-account-${++newAccountSequence}`,
  accountNumber: '',
  name: '',
  costCategoryId,
  active: true,
});

export const createEmptyGroupAccount = (): GroupAccountFormValues => ({
  id: `new-account-${++newAccountSequence}`,
  accountNumber: '',
  name: '',
  classification: 'ownerCost',
  amount: 0,
  method: 'perSquareMeter',
  active: true,
});
